import * as ngCore from '@angular/core'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../state'
import * as content from '../../../../content'

/**
 * Tenant Users tab for user profile management and access assignments.
 */
@ngCore.Component({
  imports: [
    ds.table.Component,
    ds.overlay.confirmation.Component,
    content.admin.modals.userAssignment.Component,
    content.admin.modals.user.Component,
  ],
  selector: 'atn-admin-tenant-page-users-tab',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.admin.users.Collection} */
  public readonly usersEc = ngCore.inject(state.admin.users.Collection)

  /** @see {@link state.admin.userAssignment.Feature} */
  public userAssignmentFeature = ngCore.inject(
    state.admin.userAssignment.Feature,
  )

  /** Canonical table input for users assigned to this tenant. */
  public readonly usersTableConfig = {
    collection: this.usersEc,
    view: {
      creation: () => this.addClickHandlerCallback(),
      search: true,
      title: 'Users',
    },
  } satisfies ds.table.ec.Config.Input<state.admin.users.TypeParams>

  /** Name of the user marked for deletion */
  public deletingUserNameSg = ngCore.computed(
    (
      [deletedUser] = this.usersEc.plugins.editingPlugin.feature.selectors
        .deletedEntities()
        .asSignal(),
    ) => deletedUser?.name,
  )

  /** Text displayed in delete confirmation dialog. */
  public readonly deleteConfirmationTextSg = ngCore.computed(
    (
      deletingUserName = this.deletingUserNameSg(),
    ) => `Are you sure you want to delete <b>
      ${deletingUserName}</b>? The user will be deleted and unassigned from every tenant and entity where they are assigned. This action cannot be undone.
      <br> Please type <b>
      ${deletingUserName}
      </b> in the field below to confirm this action.`,
  )

  /**
   * Handles the click event on the table's add button.
   */
  public addClickHandlerCallback = () => {
    this.userAssignmentFeature.actions.setEditingMode
      .from('Admin.Tenant.Users.Tab.Table.Add.Clicked')
      .dispatch({ isEditing: true })
  }

  /**
   * Discard changes for unassigning user
   */
  public cancelUnassignmentClickHandler(): void {
    this.userAssignmentFeature.actions.markUserForUnassignment
      .from('Admin.Tenant.Users.Tab.Confirmation.Overlay.Cancel.Button.Clicked')
      .dispatch({ user: undefined })
  }

  /**
   * Confirm user unassignment
   */
  public confirmUnassignmentClickHandler(): void {
    const deletingAssignment =
      this.userAssignmentFeature.selectors.deletingAssignment.asSignal()
    if (deletingAssignment) {
      this.userAssignmentFeature.actions.assignAccess
        .from(
          'Admin.Tenant.Users.Tab.Confirmation.Overlay.Confirm.Button.Clicked',
        )
        .dispatch({ assignments: [deletingAssignment] })
    }
  }

  /**
   * Handles the confirmation action for user deletion.
   */
  public confirmDeletionClickHandler(): void {
    this.usersEc.plugins.editingPlugin.feature.actions.submit
      .from('Admin.Tenant.Users.Tab.User.Delete.Confirmation.Confirm.Clicked')
      .dispatch({ delete: true })
  }

  /**
   * Handles the cancellation action for user deletion.
   */
  public cancelDeletionClickHandler(): void {
    this.usersEc.plugins.editingPlugin.feature.actions.discard
      .from('Admin.Tenant.Users.Tab.User.Delete.Confirmation.Cancel.Clicked')
      .dispatch({ delete: true })
  }
}
